// @LTG-ID: data/journals/walk-away-number-selling-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 22 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Personal Readiness (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "walk-away-number-selling-business",
    FRONTMATTER: {
        number: "59",
        section: "Personal Readiness",
        sectionHref: "/journals#personal-readiness",
        sectionAnchor: "personal-readiness",
        title: "What Is Your Walk-Away Number? Calculating How Much You Need From the Sale",
        deck: "Your walk-away number is the minimum after-tax, after-debt sale proceeds that, combined with your other assets, funds your post-exit life.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-31",
        publishedHuman: "May 31, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 506,
        timeRequired: "PT3M",
        keywords: "how much do I need to sell my business for to retire, walk-away number, business sale retirement calculation, net proceeds from business sale",
        articleSection: "Personal Readiness",
        articleBodyPreview: "Your walk away number is the minimum after tax, after debt sale proceeds that, combined with your other assets, funds your post exit life. Calculate it by estimating annual spending, applying a sustainable withdrawal rate to find required capital, subtracting existing assets, then grossing up for taxes, debt, and transaction costs. Every negotiation has a moment when the seller must decide whether an offer is enough. Sellers who have done this math answer in a day. Sellers who have not answer wi",
        complianceNote: "This article is educational and does not constitute financial, tax, or investment advice. Consult qualified professionals about your specific situation.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "asset-sale-vs-stock-sale",
        "personal-readiness-to-exit",
        "seller-financing-business-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "What Is Your Walk-Away Number? What You Need From the Sale",
        canonical: "https://letstalkgrowth.com/journals/walk-away-number-selling-business",
        ogImage: "/journals/og/walk-away-number-selling-business.jpg",
    },
    BODY_MARKDOWN: `Your walk-away number is the minimum after-tax, after-debt sale proceeds that, combined with your other assets, funds your post-exit life. Calculate it by estimating annual spending, applying a sustainable withdrawal rate to find required capital, subtracting existing assets, then grossing up for taxes, debt, and transaction costs.

Every negotiation has a moment when the seller must decide whether an offer is enough. Sellers who have done this math answer in a day. Sellers who have not answer with a feeling, and feelings negotiate badly.

## The calculation, in five honest steps

**Step 1: Price the life.** Write down the annual spending your next chapter actually requires, including everything the business currently absorbs: health insurance, vehicles, phone, travel that was always partly personal. Add irregular items, weddings, house projects, helping family, as a reserve. Most owners have never seen their true personal number because the company blurred it.

**Step 2: Convert spending to required capital.** Financial planners typically translate annual spending needs into a required pool of invested assets using a sustainable withdrawal rate, the percentage you can draw annually with high odds of the money outliving you. The public research on this, from Bengen's original four percent studies through Morningstar's regularly updated withdrawal-rate research, is the standard reference, and the appropriate rate depends on your horizon: a fifty-five-year-old planning forty years of withdrawals uses a more conservative rate than a seventy-year-old planning fifteen. This is exactly the conversation to have with a fee-only planner, with Social Security and any pension income reducing the burden the portfolio must carry.

**Step 3: Subtract what you already have.** Retirement accounts, investments, and income-producing real estate reduce what the sale itself must deliver. The remainder is the job the transaction has to do.

**Step 4: Gross up to a headline price.** The sale price is not the check. Business debt is repaid at closing. Transaction costs, broker or advisor fees, legal, accounting, take their share. Then taxes, which vary enormously with deal structure and entity type, as our asset versus stock sale article explains. Your CPA can model the ladder for your situation; the general lesson is that the headline price must be meaningfully larger than the net you require.

**Step 5: Stress the assumptions.** Run the version where the deal includes an earnout you might not fully collect, where markets disappoint early in retirement, where a family need arrives. If the plan only works when everything goes right, the number is not done.

## What the number buys you

The walk-away number is not a listing price and should never be confused with one; the market sets prices, not your needs. Its power is negotiating clarity. Above the number, you are choosing among good outcomes. Below it, you decline without drama and return to building value. Owners describe this as the single most calming artifact in the whole exit process, and buyers can sense the difference between a seller who knows their floor and one who is guessing.`,
    FAQ: [
        { question: "Is my walk-away number the same as my asking price?", answer: "No. The asking price comes from market evidence about what buyers pay for businesses like yours. The walk-away number comes from your personal financial requirements. When the market number exceeds the personal number, you have options; when it does not, you have a value-building agenda." },
        { question: "What withdrawal rate should I use?", answer: "Public research, including Bengen's foundational work and Morningstar's ongoing studies, frames sustainable withdrawal rates as horizon-dependent, generally in the low single digits annually. A financial planner can tailor the rate to your timeline, portfolio, and risk tolerance rather than relying on a single universal figure." },
        { question: "How much of the sale price do sellers actually keep?", answer: "It varies widely with debt levels, deal structure, entity type, and state taxes. The reliable rule is only that net proceeds are always meaningfully below the headline price, which is why the gross-up step belongs in every seller's math, done with a CPA before going to market." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Deal Process",
            title: "Asset Sale vs Stock Sale: What Every Seller Should Understand Before Negotiating",
            meta: "3 min · Brief 46",
            to: "/journals/asset-sale-vs-stock-sale",
        },
        {
            tag: "Personal Readiness",
            title: "Are You Personally Ready to Exit? The Owner's Guide to Financial and Life Readiness",
            meta: "4 min · Brief 58",
            to: "/journals/personal-readiness-to-exit",
        },
        {
            tag: "Deal Process",
            title: "Seller Financing: How It Works and When It Makes Sense",
            meta: "3 min · Brief 61",
            to: "/journals/seller-financing-business-sale",
        },
    ],
};

export default BRIEF;
